import "../global.css"

import { useEffect } from "react"
import { Slot, useNavigationContainerRef } from "expo-router"
import { Toaster } from "burnt/web"
import { focusManager } from "@tanstack/react-query"
import * as Sentry from "@sentry/react-native"
import * as Clarity from "@microsoft/react-native-clarity"
import { KeyboardProvider } from "react-native-keyboard-controller"
import { Platform, AppState, type AppStateStatus } from "react-native"
import * as SplashScreen from "expo-splash-screen"
import { ActionSheetProvider } from "@expo/react-native-action-sheet"
import { TrueSheetProvider } from "@lodev09/react-native-true-sheet"

import { TRPCProvider } from "@src/utils/trpc"
import { VersionCheck } from "@src/components/version-check"
import { ENV } from "../utils/env"
import { analytics } from "../utils/analytics"
import { GestureHandlerRootView } from "react-native-gesture-handler"
import { AlertContainer } from "@src/components/ui"

/**
 * Map the active expo-router route to a human-readable screen name.
 * Dynamic segments (e.g. `/orders/123/edit`) are reduced to their param
 * placeholders (`orders/[id]/edit`) so they group cleanly in GA4.
 */
function getActiveScreenName(navigationRef: ReturnType<typeof useNavigationContainerRef>): { name: string; class?: string } | null {
  if (!navigationRef.isReady()) return null
  // expo-router's ref is untyped here, so read the route shape defensively.
  const route = navigationRef.getCurrentRoute() as { path?: string; name?: string } | undefined
  if (!route) return null
  return { name: route.path || route.name || "unknown", class: route.name }
}

export function Root() {
  const navigationRef = useNavigationContainerRef()

  useEffect(() => {
    // Gate collection to production. iOS is already gated by the plist
    // (`IS_ANALYTICS_ENABLED`), but this also covers Android, which has no
    // equivalent native flag. Harmless no-op in __DEV__.
    analytics.setEnabled(ENV.channel === "production")
    analytics.setUserProperties({
      channel: ENV.channel,
      version: ENV.version,
      runtimeVersion: ENV.runtimeVersion,
    })

    if (!__DEV__ && Platform.OS !== "web") {
      Clarity.initialize("u9huvxx5ai", { logLevel: Clarity.LogLevel.None })
      Clarity.setCustomTag("channel", ENV.channel)
      Clarity.setCustomTag("version", ENV.version)
      Clarity.setCustomTag("runtimeVersion", ENV.runtimeVersion)
    }

    // Hide splash screen
    setTimeout(() => {
      SplashScreen.hideAsync()
    }, 1000)
  }, [])

  // Auto screen-view tracking: emit an event whenever the active route changes.
  useEffect(() => {
    if (!navigationRef) return
    const logCurrent = () => {
      const screen = getActiveScreenName(navigationRef)
      if (screen) analytics.logScreenView(screen)
    }

    // Initial screen once the navigator is ready.
    if (navigationRef.isReady()) logCurrent()
    const unsubscribe = navigationRef.addListener("state", logCurrent)
    return unsubscribe
  }, [navigationRef])

  return (
    <>
      <GestureHandlerRootView style={{ flex: 1 }}>
        <VersionCheck>
          <TrueSheetProvider>
            <ActionSheetProvider>
              <KeyboardProvider preload={false}>
                <TRPCProvider>
                  <Slot />
                  <RefetchOnAppStateChange />
                </TRPCProvider>
              </KeyboardProvider>
            </ActionSheetProvider>
          </TrueSheetProvider>
        </VersionCheck>
      </GestureHandlerRootView>
      <Toaster position="bottom-right" />
      <AlertContainer />
    </>
  )
}

function onAppStateChange(status: AppStateStatus) {
  if (Platform.OS !== "web") {
    focusManager.setFocused(status === "active")
  }
}

function RefetchOnAppStateChange() {
  useEffect(() => {
    const subscription = AppState.addEventListener("change", onAppStateChange)
    return () => subscription.remove()
  }, [])

  return null
}

export default __DEV__ ? Root : Sentry.wrap(Root)
